import React, { useState } from 'react';
import { useGenerateTdsReportQuery, useGetTdsSummaryQuery, useDownloadTdsReportMutation } from '../../services/tdsReportApi';
import { DocumentTextIcon, DocumentArrowDownIcon, CurrencyRupeeIcon, UsersIcon, CalendarIcon } from '@heroicons/react/24/outline';
import { getCurrentFinancialYear } from '../../utils/financialYear';

const TdsReports = () => {
  const [selectedQuarter, setSelectedQuarter] = useState(null);
  const [selectedFinancialYear, setSelectedFinancialYear] = useState(getCurrentFinancialYear());
  
  const { data: summary, isLoading: summaryLoading } = useGetTdsSummaryQuery();
  const { data: reportData, isLoading: reportLoading, refetch } = useGenerateTdsReportQuery({
    quarter: selectedQuarter,
    financialYear: selectedFinancialYear
  });
  const [downloadReport, { isLoading: downloadLoading }] = useDownloadTdsReportMutation();

  const handleQuarterChange = (quarter) => {
    setSelectedQuarter(quarter === selectedQuarter ? null : quarter);
  };

  const handleDownload = async () => {
    try {
      await downloadReport({
        quarter: selectedQuarter,
        financialYear: selectedFinancialYear
      }).unwrap();
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const quarters = [
    { id: 1, name: 'Q1 (Apr-Jun)', months: 'Apr - Jun' },
    { id: 2, name: 'Q2 (Jul-Sep)', months: 'Jul - Sep' },
    { id: 3, name: 'Q3 (Oct-Dec)', months: 'Oct - Dec' },
    { id: 4, name: 'Q4 (Jan-Mar)', months: 'Jan - Mar' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-4 sm:p-6 space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        <div className="w-full lg:w-auto">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-gray-900 to-indigo-900 bg-clip-text text-transparent mb-2">
            TDS Reports
          </h1>
          <p className="text-gray-600 text-sm sm:text-base lg:text-lg">Generate and view TDS deduction reports</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
          <button
            onClick={handleDownload}
            disabled={downloadLoading || !reportData}
            className="flex items-center justify-center px-4 sm:px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-xl font-medium transition-all duration-300 hover:shadow-lg hover:scale-105 text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <DocumentArrowDownIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
            {downloadLoading ? 'Downloading...' : 'Download CSV'}
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      {summaryLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
          <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-4 sm:p-6 border border-white/20 shadow-xl">
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          </div>
          <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-4 sm:p-6 border border-white/20 shadow-xl">
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
            </div>
          </div>
        </div>
      ) : summary ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
          <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-4 sm:p-6 border border-white/20 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <h3 className="text-sm sm:text-base font-semibold bg-gradient-to-r from-gray-900 to-indigo-900 bg-clip-text text-transparent mb-1">Total TDS Deducted</h3>
                <p className="text-xs sm:text-sm text-gray-600 mb-2">FY {summary.data.currentFinancialYear}</p>
                <p className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent mb-1">
                  {formatCurrency(summary.data.totalTdsDeducted)}
                </p>
                <p className="text-xs sm:text-sm text-gray-600">
                  {summary.data.totalEmployeesWithTds} employees
                </p>
              </div>
              <div className="p-3 sm:p-4 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-2xl ml-3">
                <CurrencyRupeeIcon className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-4 sm:p-6 border border-white/20 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <h3 className="text-sm sm:text-base font-semibold bg-gradient-to-r from-gray-900 to-indigo-900 bg-clip-text text-transparent mb-1">Average TDS per Month</h3>
                <p className="text-xs sm:text-sm text-gray-600 mb-2">FY {summary.data.currentFinancialYear}</p>
                <p className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent mb-1">
                  {formatCurrency(summary.data.averageTdsPerMonth)}
                </p>
                <p className="text-xs sm:text-sm text-gray-600">
                  {summary.data.tdsPercentage}% of gross salary
                </p>
              </div>
              <div className="p-3 sm:p-4 bg-gradient-to-br from-green-100 to-emerald-100 rounded-2xl ml-3">
                <CalendarIcon className="h-6 w-6 sm:h-8 sm:w-8 text-green-600" />
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* Filter Controls */}
      <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-4 sm:p-6 border border-white/20 shadow-xl">
        <h3 className="text-lg font-semibold bg-gradient-to-r from-gray-900 to-indigo-900 bg-clip-text text-transparent mb-4">Report Filters</h3>

        {/* Financial Year Selector */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">Financial Year</label>
          <select
            value={selectedFinancialYear}
            onChange={(e) => setSelectedFinancialYear(e.target.value)}
            className="w-full px-4 py-3 bg-white/50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 hover:bg-white/70"
          >
            <option value="2025-26">2025-26</option>
            <option value="2024-25">2024-25</option>
            <option value="2023-24">2023-24</option>
            <option value="2022-23">2022-23</option>
          </select>
        </div>

        {/* Quarter Selector */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">Quarter (Optional)</label>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {quarters.map((quarter) => (
              <button
                key={quarter.id}
                onClick={() => handleQuarterChange(quarter.id)}
                className={`p-3 sm:p-4 rounded-xl border-2 text-sm font-medium transition-all duration-200 hover:scale-105 ${
                  selectedQuarter === quarter.id
                    ? 'border-blue-500 bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 shadow-lg'
                    : 'border-gray-200 hover:border-gray-300 bg-white/50 text-gray-700 hover:bg-white/70'
                }`}
              >
                <div className="font-semibold">{quarter.name}</div>
                <div className="text-xs text-gray-500 mt-1">{quarter.months}</div>
              </button>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-3">
            Leave unselected to view entire financial year
          </p>
        </div>
      </div>

      {/* Report Data */}
      {reportLoading ? (
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-4 sm:p-6 border border-white/20 shadow-xl">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        </div>
      ) : reportData ? (
        <div className="space-y-6">
          {/* Report Summary */}
          <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-4 sm:p-6 border border-white/20 shadow-xl">
            <div className="flex items-center mb-6">
              <div className="p-3 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-2xl mr-3">
                <DocumentTextIcon className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold bg-gradient-to-r from-gray-900 to-indigo-900 bg-clip-text text-transparent">Report Summary</h3>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-200/50 shadow-sm hover:shadow-md transition-all duration-200">
                <p className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                  {formatCurrency(reportData.data.summary.totalTdsDeducted)}
                </p>
                <p className="text-sm text-gray-600 font-medium">Total TDS Deducted</p>
              </div>
              <div className="text-center p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border border-green-200/50 shadow-sm hover:shadow-md transition-all duration-200">
                <p className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                  {reportData.data.summary.totalEmployeesWithTds}
                </p>
                <p className="text-sm text-gray-600 font-medium">Employees with TDS</p>
              </div>
              <div className="text-center p-4 bg-gradient-to-br from-purple-50 to-violet-50 rounded-xl border border-purple-200/50 shadow-sm hover:shadow-md transition-all duration-200">
                <p className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-purple-600 to-violet-600 bg-clip-text text-transparent">
                  {reportData.data.summary.records}
                </p>
                <p className="text-sm text-gray-600 font-medium">Total Records</p>
              </div>
              <div className="text-center p-4 bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl border border-orange-200/50 shadow-sm hover:shadow-md transition-all duration-200">
                <p className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent">
                  {formatCurrency(reportData.data.summary.averageTdsPerEmployee)}
                </p>
                <p className="text-sm text-gray-600 font-medium">Avg. TDS per Employee</p>
              </div>
            </div>
          </div>

          {/* Employee-wise Details */}
          <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-4 sm:p-6 border border-white/20 shadow-xl">
            <h3 className="text-lg font-semibold bg-gradient-to-r from-gray-900 to-indigo-900 bg-clip-text text-transparent mb-4">TDS Records</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200/50">
                <thead className="bg-gradient-to-r from-blue-50 to-indigo-50">
                  <tr>
                    <th className="px-4 sm:px-6 py-4 text-left text-xs font-semibold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent uppercase tracking-wider">
                      Employee
                    </th>
                    <th className="px-4 sm:px-6 py-4 text-left text-xs font-semibold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent uppercase tracking-wider">
                      Department
                    </th>
                    <th className="px-4 sm:px-6 py-4 text-left text-xs font-semibold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent uppercase tracking-wider">
                      Period
                    </th>
                    <th className="px-4 sm:px-6 py-4 text-left text-xs font-semibold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent uppercase tracking-wider">
                      Gross Salary
                    </th>
                    <th className="px-4 sm:px-6 py-4 text-left text-xs font-semibold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent uppercase tracking-wider">
                      TDS Deducted
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white/50 divide-y divide-gray-200/50">
                  {reportData.data.records.slice(0, 10).map((record, index) => (
                    <tr key={index} className="hover:bg-white/80 transition-all duration-200 hover:shadow-sm">
                      <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium bg-gradient-to-r from-gray-900 to-indigo-900 bg-clip-text text-transparent">
                            {record.employeeName}
                          </div>
                          <div className="text-sm text-gray-500">
                            ID: {record.employeeId}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm bg-gradient-to-r from-gray-900 to-indigo-900 bg-clip-text text-transparent font-medium">
                        {record.department || 'N/A'}
                      </td>
                      <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-600 bg-white/50 px-2 py-1 rounded-lg">
                        {new Date(record.year, record.month - 1).toLocaleDateString('en-IN', {
                          year: 'numeric',
                          month: 'short'
                        })}
                      </td>
                      <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm bg-gradient-to-r from-gray-900 to-indigo-900 bg-clip-text text-transparent font-medium">
                        {formatCurrency(record.grossSalary)}
                      </td>
                      <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm font-medium bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                        {formatCurrency(record.tdsDeducted)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {reportData.data.records.length > 10 && (
                <div className="text-center py-4 border-t border-gray-200/50 bg-gradient-to-r from-blue-50/50 to-indigo-50/50 rounded-xl mt-4">
                  <p className="text-sm text-gray-600 font-medium">
                    Showing first 10 of {reportData.data.records.length} records.
                    Download CSV for complete data.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-4 sm:p-6 border border-white/20 shadow-xl">
          <div className="text-center py-12">
            <div className="p-4 bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl inline-block mb-4">
              <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
            </div>
            <h3 className="mt-2 text-sm font-medium bg-gradient-to-r from-gray-900 to-indigo-900 bg-clip-text text-transparent">No report data</h3>
            <p className="mt-1 text-sm text-gray-500">
              Select filters and generate a report to view TDS data.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default TdsReports;
